import { useId } from 'react'
import { Box, useTheme } from '@mui/material'
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import dayjs from 'dayjs'

import {
  CHART_AREA_PROPS,
  CHART_AXIS_PROPS,
  CHART_BAR_PROPS,
  ChartAreaGradient,
  chartActiveDot,
  chartAxisTick,
  chartGridProps,
  chartSeriesColor,
  chartTooltipCursor,
  chartTooltipStyle,
} from './chartStyle'

export interface TrendChartSeries {
  dataKey: string
  /** Tooltip name for the series. */
  label: string
  /** Defaults to the brand series colour. */
  color?: string
}

export interface TrendChartProps {
  /** One row per bucket, oldest first. */
  data: ReadonlyArray<Record<string, string | number | null>>
  /** The row key holding the bucket's date (`YYYY-MM-DD`). */
  xKey: string
  series: ReadonlyArray<TrendChartSeries>
  /**
   * `area` for a daily trend, `bar` for discrete totals (months). Billing's rule: a smoothed
   * curve through a handful of totals invents values between them.
   */
  mark?: 'area' | 'bar'
  height?: number
  ariaLabel: string
  formatValue?: (n: number) => string
  /**
   * The tooltip's heading for a bucket. Defaults to the full day ("Mon, Sep 29, 2026"); a
   * weekly series passes "Week of Sep 29" so a bucket is not read as a single day.
   */
  formatLabel?: (bucket: string) => string
}

const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 })
const full = new Intl.NumberFormat('en-US')

const MARGIN = { top: 4, right: 4, left: 0, bottom: 0 }

/**
 * A small time series in the house chart style (`chartStyle`), for cards that need a trend
 * rather than the full `/analytics` chart: no brush, no legend, no granularity control.
 * The y-axis stays visible because on these cards the magnitude is part of the finding and
 * the tooltip would otherwise be the only way to read it.
 *
 * With one series the area carries the brand gradient; with several the fills go
 * transparent so overlapping gradients do not muddy each other (the `/analytics` rule).
 */
export const TrendChart = ({
  data,
  xKey,
  series,
  mark = 'area',
  height = 140,
  ariaLabel,
  formatValue = (n) => full.format(n),
  formatLabel = (v) => dayjs(v).format('ddd, MMM D, YYYY'),
}: TrendChartProps) => {
  const theme = useTheme()
  // useId's `:r0:` form is not a safe url() fragment, so strip it to word characters.
  const idBase = `trend-${useId().replace(/[^a-zA-Z0-9]/g, '')}`
  if (data.length === 0) return null
  const single = series.length === 1
  const colorOf = (s: TrendChartSeries) => s.color ?? chartSeriesColor(theme)
  return (
    <Box role="img" aria-label={ariaLabel} sx={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data as Array<Record<string, string | number | null>>} margin={MARGIN}>
          {mark === 'area' && single ? (
            <defs>
              <ChartAreaGradient id={`${idBase}-0`} color={colorOf(series[0])} />
            </defs>
          ) : null}
          <CartesianGrid {...chartGridProps(theme)} />
          <XAxis
            dataKey={xKey}
            {...CHART_AXIS_PROPS}
            tick={chartAxisTick(theme)}
            tickFormatter={(v: string) => dayjs(v).format('MMM D')}
            minTickGap={24}
          />
          <YAxis
            {...CHART_AXIS_PROPS}
            tick={chartAxisTick(theme)}
            tickFormatter={(v: number) => compact.format(v)}
            allowDecimals={false}
            width={40}
          />
          <Tooltip
            {...chartTooltipStyle(theme)}
            cursor={chartTooltipCursor(theme, mark === 'bar' ? 'bar' : 'line')}
            labelFormatter={(v: string) => formatLabel(v)}
            formatter={(v: number | string) => (typeof v === 'number' ? formatValue(v) : v)}
          />
          {series.map((s) =>
            mark === 'bar' ? (
              <Bar
                key={s.dataKey}
                dataKey={s.dataKey}
                name={s.label}
                fill={colorOf(s)}
                {...CHART_BAR_PROPS}
              />
            ) : (
              <Area
                key={s.dataKey}
                {...CHART_AREA_PROPS}
                dataKey={s.dataKey}
                name={s.label}
                stroke={colorOf(s)}
                fill={single ? `url(#${idBase}-0)` : 'transparent'}
                fillOpacity={1}
                activeDot={chartActiveDot(theme, colorOf(s))}
                // Off, as billing's area: a small card chart gains nothing from an entry
                // animation, and under a throttled rAF it would draw late.
                isAnimationActive={false}
              />
            ),
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  )
}
